import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Cookie, X } from 'lucide-react';

function CookieBanner({ onClose }) {
  const { t } = useTranslation();
  const [preferences, setPreferences] = useState({
    necessary: true,
    functional: false,
    analytics: false
  });

  useEffect(() => {
    // Load existing preferences if available
    const consent = localStorage.getItem('cookieConsent');
    if (consent) {
      const savedPrefs = JSON.parse(consent);
      setPreferences(savedPrefs.preferences);
    }
  }, []);

  const loadGoogleAnalytics = () => {
    // Initialize Google Analytics if not already loaded
    if (!window.gtag) {
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://www.googletagmanager.com/gtag/js?id=YOUR_GA_ID';
      document.head.appendChild(script);

      window.dataLayer = window.dataLayer || [];
      function gtag(){window.dataLayer.push(arguments);}
      window.gtag = gtag;
      gtag('js', new Date());
      gtag('config', 'YOUR_GA_ID');
    }
  };

  const handleCheckboxChange = (type) => {
    if (type === 'necessary') return; // Cannot uncheck necessary cookies
    setPreferences({
      ...preferences,
      [type]: !preferences[type]
    });
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true
    };
    saveConsent(allAccepted);
    if (onClose) onClose();
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
    if (onClose) onClose();
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      functional: false,
      analytics: false
    };
    saveConsent(onlyNecessary);
    if (onClose) onClose();
  };

  const saveConsent = (prefs) => {
    const consent = {
      preferences: prefs,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    
    // Load Google Analytics if accepted
    if (prefs.analytics) {
      loadGoogleAnalytics();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end md:items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full p-6 md:p-8 animate-slide-up">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Cookie className="text-sky-600" size={24} />
            <h2 className="text-2xl font-bold text-gray-800">{t('app.cookies.title')}</h2>
          </div>
          <button
            onClick={handleRejectAll}
            className="text-gray-400 hover:text-gray-600 transition"
            aria-label={t('app.cookies.close')}
          >
            <X size={24} />
          </button>
        </div>

        <p className="text-gray-600 mb-6">
          <Trans
            i18nKey="app.cookies.description"
            components={{
              link: <Link to="/cookie-policy" className="text-sky-600 hover:text-sky-700 underline" target="_blank" />
            }}
          />
        </p>

        <div className="space-y-4 mb-6">
          {/* Necessary Cookies */}
          <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              id="necessary"
              checked={preferences.necessary}
              disabled
              className="mt-1 h-5 w-5 text-sky-500 border-gray-300 rounded cursor-not-allowed"
            />
            <div className="flex-1">
              <label htmlFor="necessary" className="font-semibold text-gray-800 cursor-default">
                {t('app.cookies.necessaryTitle')}
              </label>
              <p className="text-sm text-gray-600 mt-1">
                {t('app.cookies.necessaryDescription')}
              </p>
            </div>
          </div>

          {/* Functional Cookies */}
          <div className="flex items-start space-x-3 p-4 border border-gray-200 rounded-lg hover:border-sky-300 transition">
            <input
              type="checkbox"
              id="functional"
              checked={preferences.functional}
              onChange={() => handleCheckboxChange('functional')}
              className="mt-1 h-5 w-5 text-sky-500 border-gray-300 rounded focus:ring-sky-500"
            />
            <div className="flex-1">
              <label htmlFor="functional" className="font-semibold text-gray-800 cursor-pointer">
                {t('app.cookies.functionalTitle')}
              </label>
              <p className="text-sm text-gray-600 mt-1">
                {t('app.cookies.functionalDescription')}
              </p>
            </div>
          </div>

          {/* Analytics Cookies */}
          <div className="flex items-start space-x-3 p-4 border border-gray-200 rounded-lg hover:border-sky-300 transition">
            <input
              type="checkbox"
              id="analytics"
              checked={preferences.analytics}
              onChange={() => handleCheckboxChange('analytics')}
              className="mt-1 h-5 w-5 text-sky-500 border-gray-300 rounded focus:ring-sky-500"
            />
            <div className="flex-1">
              <label htmlFor="analytics" className="font-semibold text-gray-800 cursor-pointer">
                {t('app.cookies.analyticsTitle')}
              </label>
              <p className="text-sm text-gray-600 mt-1">
                {t('app.cookies.analyticsDescription')}
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-3">
          <button
            onClick={handleRejectAll}
            className="flex-1 py-3 px-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition"
          >
            {t('app.cookies.necessaryOnly')}
          </button>
          <button
            onClick={handleSavePreferences}
            className="flex-1 py-3 px-4 bg-gray-800 text-white font-semibold rounded-lg hover:bg-gray-900 transition"
          >
            {t('app.cookies.save')}
          </button>
          <button
            onClick={handleAcceptAll}
            className="flex-1 py-3 px-4 bg-gradient-to-r from-sky-500 to-emerald-500 text-white font-semibold rounded-lg hover:from-sky-600 hover:to-emerald-600 transition"
          >
            {t('app.cookies.acceptAll')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CookieBanner;
